import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../../lib/data";

const lineAnim = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { delay: 0.25 + i * 0.14, duration: 1, ease: [0.22, 1, 0.36, 1] },
    }),
};

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
    const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-ink">
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <img
                    src={IMAGES.hero}
                    alt="Fresh pink cotton candy held high against the sky"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />
            </motion.div>

            <motion.div
                style={{ y: cardY }}
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="hidden lg:block absolute right-16 bottom-40 w-64 rotate-3 z-10"
            >
                <div className="rounded-3xl overflow-hidden border-4 border-white shadow-2xl">
                    <img src={IMAGES.popcorn} alt="Fresh gourmet popcorn" className="w-full aspect-[4/5] object-cover" />
                </div>
                <p className="mt-3 text-center font-oswald uppercase tracking-[0.3em] text-[10px] text-white/80">
                    Popped fresh. Right now.
                </p>
            </motion.div>

            <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 md:px-10 pb-28 md:pb-32 pt-40">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15, duration: 0.8 }}
                    className="font-manrope text-xs md:text-sm font-bold tracking-[0.4em] uppercase text-pink mb-6"
                >
                    Cotton Candy • Popcorn • Good Times
                </motion.p>
                <h1 className="font-oswald uppercase text-white leading-[0.85] tracking-tight text-[19vw] sm:text-[15vw] lg:text-[10.5rem]">
                    <span className="mask-line">
                        <motion.span custom={0} variants={lineAnim} initial="hidden" animate="show" className="block">
                            Bring
                        </motion.span>
                    </span>
                    <span className="mask-line">
                        <motion.span custom={1} variants={lineAnim} initial="hidden" animate="show" className="block text-pink">
                            The Show.
                        </motion.span>
                    </span>
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-7 max-w-xl text-base md:text-lg text-zinc-200 leading-relaxed"
                >
                    Fresh cotton candy, popcorn and snack experiences made for parties, events and good times.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/book"
                        data-testid="hero-book-button"
                        className="inline-flex items-center gap-2 rounded-full bg-pink px-8 py-4 font-oswald uppercase tracking-widest text-ink hover:bg-white transition-colors duration-300 active:scale-95"
                    >
                        Book The Show <ArrowRight size={16} />
                    </Link>
                    <Link
                        to="/shop"
                        data-testid="hero-shop-button"
                        className="inline-flex items-center gap-2 rounded-full border-2 border-white px-8 py-4 font-oswald uppercase tracking-widest text-white hover:bg-white hover:text-ink transition-colors duration-300 active:scale-95"
                    >
                        Shop The Fun
                    </Link>
                    <Link
                        to="/wholesale"
                        data-testid="hero-wholesale-link"
                        className="font-manrope text-sm font-bold tracking-[0.2em] uppercase text-white underline underline-offset-8 decoration-pink decoration-2 hover:text-pink transition-colors duration-300"
                    >
                        Wholesale
                    </Link>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2"
            >
                <span className="font-oswald text-[10px] tracking-[0.4em] uppercase text-white/60">Scroll</span>
                <motion.span
                    animate={{ scaleY: [1, 0.4, 1] }}
                    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                    className="block h-10 w-px bg-pink origin-top"
                />
            </motion.div>
        </section>
    );
};
